import { TemplateType } from "@redotech/redo-marketing-templates-common/email-builder";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import { sendSmsFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isSendSmsStepValid,
  SendSmsState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import SmsIcon from "@redotech/redo-web/icon-old/send-sms.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";
import { getTemplateTypeLabel } from "./template-type-label";

export const SEND_SMS = (
  Details: React.ComponentType<StepTypeDetailsProps<SendSmsState>>,
): StepType<SendSmsState, ModelStep.SendSms> => ({
  Details: (props: StepTypeDetailsProps<SendSmsState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: STEP_TYPE_NAMES[ModelStep.SEND_SMS],
  valid(state) {
    return isSendSmsStepValid(state).valid;
  },
  Icon: SmsIcon,
  description(state) {
    return state?.smsName && state?.smsType
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        `${state.smsName} - ${getTemplateTypeLabel(state.smsType as TemplateType)}`
      : "";
  },
  empty: { next: undefined, smsId: "" },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(`Invalid handle index ${handleIndex} for Send SMS step`);
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      smsId: state.smsId,
      type: ModelStep.SEND_SMS,
      next: state.next ? id(state.next) : undefined,
    };
  },
  fromModel(model, id) {
    return sendSmsFromModel(model, id);
  },
});
